import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { toast } from "sonner";
import { MemoryCard } from "@/components/MemoryCard";
import { Heart, Sparkles, Plus, Loader2 } from "lucide-react";

export default function Memories() {
  const { user } = useAuth();
  const [memories, setMemories] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.memories({}).then(({ data }) => setMemories(data));
  useEffect(() => { load(); }, []);

  const like = async (id) => {
    if (!user) { toast("Sign in to react"); return; }
    const { data } = await api.reactMemory(id, "like");
    setMemories((m) => m.map((x) => x.id === id ? { ...x, likes: data.likes, loves: data.loves } : x));
  };

  return (
    <div data-testid="memories-page" className="max-w-[1300px] mx-auto px-5 sm:px-8 pt-12">
      <Seo
        title="Wisconsin Prom Memories & Lookbooks"
        description="Browse WI prom night memories & best-dressed galleries. Share your look, tag your school, get AI caption ideas."
        path="/memories"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Memories", path: "/memories" }])}
      />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Memories", path: "/memories" }]} />
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="font-label text-[10px] text-rosemetal mb-2">The night, remembered</p>
          <h1 className="font-display text-5xl sm:text-6xl">Prom Memories</h1>
        </div>
        <button data-testid="share-memory-btn" onClick={() => user?.school_verified ? setOpen(true) : toast.error("Verify your school email to share")} className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Share a Memory</button>
      </div>

      {memories.length === 0 ? <p className="font-body text-muted-foreground py-20 text-center">No memories yet — the gallery awaits your night.</p> :
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {memories.map((m) => (
            <MemoryCard key={m.id} memory={m} user={user} onDeleted={load} />
          ))}
        </div>}

      {open && <ShareModal onClose={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ShareModal({ onClose }) {
  const [form, setForm] = useState({ image_url: "", caption: "", event: "prom", season: "2026" });
  const [captions, setCaptions] = useState([]);
  const [busy, setBusy] = useState(false);

  const genCaptions = async () => {
    const { data } = await api.caption("prom night editorial glam");
    setCaptions(data.captions || []);
  };

  const submit = async () => {
    if (!form.image_url) { toast("Paste a photo URL"); return; }
    setBusy(true);
    try { await api.createMemory(form); toast.success("Submitted for moderation — it'll appear once approved."); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="share-memory-modal" className="bg-ivory rounded-sm max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-3xl mb-6">Share your night</h3>
        <input data-testid="memory-image-url" placeholder="Photo URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm mb-3 outline-none focus:border-rosemetal" />
        <input data-testid="memory-caption" placeholder="Caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm mb-2 outline-none focus:border-rosemetal" />
        <button data-testid="gen-caption-btn" onClick={genCaptions} className="font-label text-[9px] text-rosemetal flex items-center gap-1 mb-3"><Sparkles className="w-3 h-3" /> AI caption ideas</button>
        {captions.length > 0 && <div className="flex flex-wrap gap-2 mb-4">{captions.map((c, i) => <button key={i} onClick={() => setForm({ ...form, caption: c })} className="text-xs bg-champagne/50 px-2 py-1 rounded-sm">{c}</button>)}</div>}
        <button data-testid="memory-submit" onClick={submit} disabled={busy} className="w-full bg-ink text-ivory py-3.5 font-label text-[10px] rounded-sm flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit</button>
      </div>
    </div>
  );
}
